import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import withWidth, { isWidthDown, isWidthUp } from '@material-ui/core/withWidth';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';

import cloneDeep from 'lodash/cloneDeep';
import flowRight from 'lodash/flowRight';
import get from 'lodash/get';

import { styles } from './CreateEditModifierStyles';
import { CreateEditOptionDialog, DIALOG_TYPES } from '../../share/CreateEditOptionDialog';
import UseExistingOptionDialog from './UseExistingOptionDialog';

import DisplayResources from '../DisplayResources';
import * as types from '../../../redux/resource/ResourcesActionTypes';
import * as CompanyFeatures from '../../../services/CompanyFeatures';
import * as MultiAccountAdminRedirects from '../../../services/MultiAccountAdminRedirects';
import * as Authorization from '../../../utils/AuthorizationChecks';
import * as FormSubmits from '../../../utils/FormSubmits';
import * as Functions from '../../../utils/Functions';
import * as FieldValidation from '../CreateAndEditResource/FieldValidation';
import * as utilFunctions from '../../../utils/Functions';
import SearchableSelect from '../../share/SearchableSelect';
import { optionPaginationCount, resourceFields } from '../../../data/Constants';

import { resourceFormStyle } from '../../../css/style';
import ToolTip from '../../share/ToolTip';
import { buildSearchableEntities, getSearchableEntities } from '../../../domains/Restrictions';

let resourceFieldsBySection = [];
let fieldKeysForIteration = [];
let resourceFieldsAsMap = {};

const getOptionsPath = `options?start=0&count=${optionPaginationCount}&view=full`;

const IOSSwitch = withStyles(resourceFormStyle().iosSwitchStylesForSectionedFields)(Switch);

resourceFields.customizations.forEach((field) => {
  const { key, createEditPageSectionIndex, hideInEdit } = field;
  if (hideInEdit) return;
  fieldKeysForIteration.push(key);
  resourceFieldsAsMap[key] = field;
  if (!createEditPageSectionIndex) return;
  let pageSectionFields = resourceFieldsBySection[createEditPageSectionIndex];
  if (!pageSectionFields) {
    let newPageSectionFields = [];
    newPageSectionFields.push(field);
    resourceFieldsBySection[createEditPageSectionIndex] = newPageSectionFields;
    return;
  }
  pageSectionFields.push(field);
});

const { getFieldIsInvalidStateKey, getInvalidFieldMessageStateKey } = FieldValidation;

const FieldHeader = ({
  classes,
  field,
  largeTitle,
  width,
}) => {
  const titleStyleKey = largeTitle ? 'largeFieldTitle' : 'fieldTitle';
  const fieldTitleClasses = { root: classes[titleStyleKey] };
  return (
    <section style={{ alignItems: 'center', display: 'flex' }}>
      <Typography classes={fieldTitleClasses}>{field.title}</Typography>
      <ToolTip field={field} width={width} />
    </section>
  );
};

class BaseCreateEditModifier extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    if (!props.customizations) {
      this.getSearchableOptions(props)
        .then(() => this.getSearchableModifiers(props));
    } else {
      this.initializeFields().then((firstState) => {
        const searchableCustomizations = buildSearchableEntities(props.customizations);
        firstState.searchableCustomizations = searchableCustomizations;
        this.setState(firstState);
        this.getAttachedItemsState();
      });
    }
  }

  initializeIfJustFetchedCustomizations = async (prevProps) => {
    const prevCustomizations = get(prevProps, 'customizations');
    const currentCustomizations = get(this.props, 'customizations');

    if (!prevCustomizations && currentCustomizations) {
      const newState = await this.initializeFields();
      this.setState(newState);
      this.getAttachedItemsState();
    }
  }

  scrollToOptionsAfterCreateModifier = () => {
    const { hasScrolledToOptionTitle } = this.state;
    const isFromCreateModifier = get(this.props, 'location.state.fromCreateModifier');
    if (isFromCreateModifier && this.optionScrollTarget && !hasScrolledToOptionTitle) {
      this.setState({ scrollToOptionTitle: true });
    }
  }

  /**
   * On Save and Add Options button, or hitting browser back/forwards button between
   * two Create/Edit Modifier pages. Since the route loads the same component,
   * many state values are conserved, such as the searchable items, locations,
   * modifiers
   */
  reinitializeStateIfChangeUrl = async (prevProps) => {
    const currentUrl = get(this.props, 'location.pathname');
    const prevUrl = get(prevProps, 'location.pathname');
    if (currentUrl !== prevUrl) {
      const firstState = await this.initializeFields();
      firstState.hasScrolledToOptionTitle = false;
      this.setState(firstState);
      this.getAttachedItemsState();
    }
  }

  async componentDidUpdate(prevProps) {
    const prevApiToken = prevProps.apiToken;
    const { apiToken, actions, history } = this.props;
    if (prevApiToken && apiToken !== prevApiToken) {
      const redirectArgs = {
        apiToken,
        actions,
        resource: 'customizations',
        history,
        locationStateParams: { fromCreateEditResource: true },
      };
      MultiAccountAdminRedirects.redirectToResourcePageWithRefresh(redirectArgs);
    }
    await this.initializeIfJustFetchedCustomizations(prevProps);
    await this.reinitializeStateIfChangeUrl(prevProps);
    this.scrollToOptionsAfterCreateModifier();
  }

  addElementToList = (newElement, listStateKey) => {
    const { [listStateKey]: listValue } = this.state;
    const newList = Array.isArray(listValue)
      ? [...listValue, newElement]
      : [newElement];
    this.setState({ [listStateKey]: newList });
  }

  addOptionToList = (newOption) => {
    this.addElementToList(newOption, 'options');
  }

  addRequestToList = (newRequestInfo) => {
    this.addElementToList(newRequestInfo, 'optionsRequestsInfo');
  }

  getSearchableModifiers = async (props) => {
    const { actions, apiToken } = props;
    const res = await actions.getAllResources(apiToken, 'customizations');
    const searchableCustomizations = getSearchableEntities(res);
    this.setState({ searchableCustomizations });
  }

  getSearchableOptions = async (props) => {
    const { actions, apiToken } = props;
    const optionsRes = await actions.getAllResourcesRecursive(apiToken, [], getOptionsPath);
    const options = get(optionsRes, 'response.entityList');
    const allOptionsMap = Functions.getIdToEntityMap(options);
    const searchableOptions = buildSearchableEntities(options);
    this.setState({ allOptionsMap, searchableOptions });
  }

  initializeFields = () => {
    const { match, type, customizations } = this.props;
    let newFormState = {
      disableSaveButton: false,
      isDialogOpen: false,
      whichDialogOpen: '',
    };

    this.getSearchableOOSLocations(this.props.actions, this.props.apiToken);

    return this.getSearchableOptions(this.props)
      .then((result) => {
        if (type === 'create') newFormState = this.initializeStateForCreate(newFormState);
        if (type === 'edit') newFormState = this.initializeStateForEdit(customizations, match, newFormState);
        return newFormState;
      });
  }

  initializeStateForCreate = (newFormState) => {
    resourceFields.customizations.forEach((field) => {
      const { key } = field;
      if (field.hideInEdit) return;
      if (!field.type || field.type === 'longText') newFormState[key] = '';
      if (['number', 'decimal'].includes(field.type)) newFormState[key] = 0;
      if (field.type === 'boolean') newFormState[key] = false;
      if (field.type === 'searchable') newFormState[key] = null;
      if (key === 'options') newFormState[key] = [];
      if (key === 'position') {
        newFormState[key] = 0;
        newFormState.startingPosition = 0;
      }

      newFormState[getFieldIsInvalidStateKey(key)] = false;
      newFormState[getInvalidFieldMessageStateKey(key)] = '';
    });
    return newFormState;
  }

  initializeStateForEdit = (customizations, match, newFormState) => {
    const fakeResources = { customizations };
    const { resourceId } = match.params;
    const fakeMatch = { params: { resource: 'customizations', resourceId } };
    const findArgs = { match: fakeMatch, resources: fakeResources };
    const currentCustomization = Functions.findEntityByID(findArgs);

    // Important: Deep clone so that we can freely edit the values we copy into state
    // without it accessing a nested object in the Redux store used in List Customizations
    const customizationClone = cloneDeep(currentCustomization);
    const { options, ...restCustomizationClone } = customizationClone;
    newFormState.originalModifierDetails = restCustomizationClone;

    resourceFields.customizations.forEach((field) => {
      const { key } = field;
      if (field.hideInEdit && key !== 'options') return;

      // Allow this.getAttachedItemsState to initialize, it may take a GET /products request
      if (key === 'productItems') return;
      const currentValue = customizationClone[key];

      newFormState[key] = currentValue;

      if (key === 'position') newFormState.startingPosition = currentValue;
      if (key === 'options') {
        const { allOptionsMap } = this.state;
        newFormState.options = Array.isArray(currentValue) && currentValue.map(option => (allOptionsMap ? allOptionsMap[option.id] : option));
      }
      if (field.type === 'searchable') newFormState[key] = null;
      newFormState[getFieldIsInvalidStateKey(key)] = false;
      newFormState[getInvalidFieldMessageStateKey(key)] = '';
    });
    return newFormState;
  }

  getAttachedItemsState = () => {
    const { actions, apiToken, match } = this.props;
    const { resourceId } = match.params;

    actions.getAllResources(apiToken, 'products')
      .then((res) => {
        if (res.error) return;
        const { attachedItemIds, allItemCustomizationsAndProductIds } = Functions.getItemsAttachedToCustomization(res, resourceId);
        const { searchableProductItems, itemNamesForIds } = Functions.buildUpSearchableProductsAndItems(res, true);
        const productItems = searchableProductItems.filter(searchableItem => attachedItemIds.get(searchableItem.value));
        this.setState({
          searchableProductItems,
          itemNamesForIds,
          productItems,
          allItemCustomizationsAndProductIds,
          originalAttachedItems: attachedItemIds,
        });
      });
  }

  getSearchableOOSLocations(actions, apiToken) {
    actions.getAllResources(apiToken, 'locations').then((res) => {
      let searchableLocations = getSearchableEntities(res);
      const searchableLocationsWithSelectAll = searchableLocations.slice();
      searchableLocationsWithSelectAll.unshift({ label: 'All Locations', value: 'all' });
      this.setState({ searchableLocations, searchableLocationsWithSelectAll });
    });
  }

  pageTitleAndSaveButtonParentClasses = { container: this.props.classes.pageTitleAndSaveButtonParent }

  pageTitleClasses = { root: this.props.classes.pageTitle }

  pageMainSectionClasses = { container: this.props.classes.pageMainSectionParent }

  sectionTitleClasses = { root: this.props.classes.sectionTitle }

  modifierFieldsPaperClasses = { root: this.props.classes.modifierFieldsPaper }

  addOptionButtonClasses = { root: this.props.classes.primaryButton }

  useExistingOptionButtonClasses = { root: this.props.classes.secondaryButton }

  saveModifierButtonClasses = { outlinedPrimary: this.props.classes.saveModifierButton, disabled: this.props.classes.disabledSaveModifierButton }

  fieldSectionParentClasses = { container: this.props.classes.fieldSectionParent }

  lastFieldSectionParentClasses = { container: this.props.classes.lastFieldSectionParent }

  inputClasses = { root: this.props.classes.textFieldMuiInput }

  textFieldFormControlClasses = { root: this.props.classes.textFieldFormControl }

  multilineTextFieldFormControlClasses = { root: this.props.classes.multilineTextFieldFormControl }

  textFieldInputProps = { disableUnderline: true, classes: this.inputClasses }

  errorBorderClasses = { root: this.props.classes.errorBorder }

  validationErrorClasses = { root: this.props.classes.validationError }

  fieldTitleClasses = { root: this.props.classes.fieldTitle }

  largeFieldTitleClasses = { root: this.props.classes.largeFieldTitle }

  isFieldInvalid = (field, val) => {
    const fieldType = field.type;
    const isRequired = field.isRequired;
    if (isRequired && !val) return true;
    const numberValidations = FieldValidation.validateNumberTypes(val, field);

    if (field.key === 'position') {
      const { startingPosition } = this.state;
      return FieldValidation.isPositionInvalid(startingPosition, numberValidations);
    }
    if (['number', 'decimal'].includes(fieldType)) {
      if (fieldType === 'number') return numberValidations.invalidInt;
      if (fieldType === 'decimal') return numberValidations.invalidDecimal;
    }
    return false;
  }

  onChangeItems = (value) => {
    this.setState({ productItems: value });
  }

  onChangeSwitch = fieldKey => (event) => {
    const newIsChecked = get(event, 'target.checked', false);
    this.setState({ [fieldKey]: newIsChecked });
  }

  onChangeTextField = fieldKey => (event) => {
    const value = get(event, 'target.value', '');
    this.setState({ [fieldKey]: value });
    this.validateFieldAndUpdateUI(fieldKey, value);
  }

  onClickDeleteOption = async (optionId) => {
    const { actions, apiToken, match } = this.props;
    const { type } = this.props;
    const { resourceId } = match.params;

    if (type === 'edit') {
      const actionObj = await actions.deleteResource(apiToken, 'customizations', resourceId, 'options', optionId);
      if (actionObj.type !== types.DELETE_RESOURCE_SUCCESS) return;
      const optionsClone = cloneDeep(this.state.options);
      const optionRemoved = optionsClone.filter(option => option.id !== Number(optionId));
      this.setState({ options: optionRemoved });
    }
  }

  onClickDuplicateOption = (newOption) => {
    const { type } = this.props;
    if (type === 'edit') this.addOptionToList(newOption);
  }

  onClickEditOption = (optionId) => {
    this.openCreateEditOptionDialog(DIALOG_TYPES.EDIT, optionId)();
  }

  openCreateEditOptionDialog = (createOrEditOption, optionId) => () => {
    this.setState({
      createOrEditOption,
      isDialogOpen: true,
      optionId,
      whichDialogOpen: 'createEditOption',
    });
  }

  openUseExistingOptionDialog = () => this.setState({ isDialogOpen: true, whichDialogOpen: 'useExistingOption' })

  closeDialogs = () => this.setState({ isDialogOpen: false, whichDialogOpen: '', optionId: null })

  prepareModifierForSubmit = () => {
    const customization = {};

    fieldKeysForIteration.forEach((fieldKey) => {
      const { type: fieldType } = resourceFieldsAsMap[fieldKey];
      const formValue = this.state[fieldKey];

      if (['number', 'decimal'].includes(fieldType)) return customization[fieldKey] = Number(formValue);
      customization[fieldKey] = formValue;
    });

    const { options, ...restCustomization } = customization;
    return restCustomization;
  }

  renderTextField = (field) => {
    const fieldKey = field.key;
    const value = this.state[fieldKey];
    const { classes, width } = this.props;

    const hasError = this.state[getFieldIsInvalidStateKey(fieldKey)];
    const isInvalidMessage = this.state[getInvalidFieldMessageStateKey(fieldKey)];
    return (
      <section className={classes.singleFieldParent}>
        <FieldHeader
          classes={classes}
          field={field}
          width={width}
        />
        <TextField
          classes={hasError ? this.errorBorderClasses : this.textFieldFormControlClasses}
          InputProps={this.textFieldInputProps}
          onChange={this.onChangeTextField(fieldKey)}
          value={value}
        />
        <Typography classes={this.validationErrorClasses}>{isInvalidMessage}</Typography>
      </section>
    );
  }

  renderSwitch = (field) => {
    const { width, classes } = this.props;
    const value = this.state[field.key];
    if (field.key === 'isSingleSelectDefault' && !this.shouldShowIsSingleSelectDefault()) return null;

    return (
      <React.Fragment>
        <section className={classes.fullWidthSwitchFieldParent}>
          <FieldHeader
            classes={classes}
            field={field}
            largeTitle
            width={width}
          />
          <IOSSwitch
            checked={value}
            onChange={this.onChangeSwitch(field.key)}
          />
        </section>
        {isWidthUp('md', width) && <Grid item xs={6} />}
      </React.Fragment>
    );
  }

  renderLongText = (field) => {
    const fieldKey = field.key;
    const value = this.state[fieldKey];
    const { classes, width } = this.props;

    return (
      <section className={classes.fullWidthSingleFieldParent}>
        <FieldHeader
          classes={classes}
          field={field}
          width={width}
        />
        <TextField
          classes={this.multilineTextFieldFormControlClasses}
          InputProps={this.textFieldInputProps}
          multiline
          onChange={this.onChangeTextField(fieldKey)}
          value={value}
        />
      </section>
    );
  }

  renderSearchable = (field) => {
    const { classes, width } = this.props;
    const { productItems, searchableProductItems } = this.state;

    return (
      <section className={classes.fullWidthSingleFieldParent}>
        <FieldHeader
          classes={classes}
          field={field}
          width={width}
        />
        <SearchableSelect
          isDisabled={!Array.isArray(searchableProductItems)}
          isMulti
          maxMenuHeight={700}
          options={searchableProductItems}
          onChange={this.onChangeItems}
          value={productItems}
        />
      </section>
    );
  }

  renderFields = sectionOfFields => sectionOfFields.map((field) => {
    let FieldByType = null;
    if (!field.type || ['number', 'decimal', 'nonZeroInt'].includes(field.type)) FieldByType = this.renderTextField(field);
    if (field.type === 'longText') FieldByType = this.renderLongText(field);
    if (field.type === 'boolean') FieldByType = this.renderSwitch(field);
    if (field.type === 'searchable') FieldByType = this.renderSearchable(field);
    return FieldByType;
  })

  replaceOptionInList = (newOption) => {
    let optionsClone = this.state.options;
    const targetOptionIndex = optionsClone.findIndex(({ id }) => newOption.id === id);
    if (targetOptionIndex === -1) return;
    optionsClone.splice(targetOptionIndex, 1, newOption);
    this.setState({ options: optionsClone });
  }

  shouldShowIsSingleSelectDefault = () => {
    const { minOptions, maxOptions } = this.state;
    return Number(minOptions) === 1 && Number(maxOptions) === 1;
  }

  renderFieldSections = () => {
    const { classes, width } = this.props;

    return resourceFieldsBySection.map((sectionOfFields, index, array) => {
      const hasIsSingleSelectDefault = sectionOfFields[0].key === 'isSingleSelectDefault';
      if (hasIsSingleSelectDefault && !this.shouldShowIsSingleSelectDefault()) return null;

      const parentClasses = index === array.length - 1 ? classes.lastFieldSectionParent : classes.fieldSectionParent;
      const halfOrFullWidth = isWidthUp('md', width) ? classes.halfPaperWidthFieldGroup : classes.fullPaperWidthFieldGroup;

      return (
        <section className={parentClasses}>
          <section className={halfOrFullWidth}>
            {this.renderFields(sectionOfFields)}
          </section>
          {isWidthUp('md', width) && <section className={classes.halfPaperWidthFieldGroup} />}
        </section>
      );
    });
  }

  shouldDisableSaveButton = (currentFieldKey, isCurrentFieldInvalid) => {
    if (isCurrentFieldInvalid) return true;
    const isAnyFieldInvalid = fieldKeysForIteration.some((fieldKey) => {
      if (fieldKey !== currentFieldKey) return this.state[getFieldIsInvalidStateKey(fieldKey)];
      return isCurrentFieldInvalid;
    });
    return isAnyFieldInvalid;
  }

  goToListCustomizations = () => {
    const { history } = this.props;
    history.push('/customizations');
  }

  goToEditPageOfSameModifier = (modifierId) => {
    const { history } = this.props;
    this.setState({ hasScrolledToOptionTitle: false });
    history.push(`/customizations/${modifierId}/edit`, { fromCreateModifier: true });
  }

  submitModifier = finalRedirectFunc => async () => {
    const isAnyFieldInvalid = this.validateFieldsBeforeSubmit();
    if (isAnyFieldInvalid) return;
    const customization = this.prepareModifierForSubmit();

    const { actions, apiToken } = this.props;
    const { type } = this.props;
    const { resourceId } = this.props.match.params;
    const rightAction = type === 'create' ? 'addResource' : 'updateResource';
    const resourcePath = type === 'create' ? ['customizations'] : ['customizations', resourceId];
    if (rightAction === 'updateResource') {
      const options = get(customization, 'options', null);
      if (options) {
        const optionsCopy = cloneDeep(options);
        customization.options = utilFunctions.removeKeysFromObjectArray(optionsCopy, ['customizations', 'outOfStockLocations']);
      }
    }
    const res = await actions[rightAction](apiToken, customization, ...resourcePath);
    const newCustomization = get(res, 'response');
    if (!newCustomization) {
      return;
    }

    const { originalAttachedItems, allItemCustomizationsAndProductIds } = this.state;
    const setItemsArgs = {
      actions,
      allItemCustomizationsAndProductIds,
      apiToken,
      customization,
      originalAttachedItems,
      resourceId: newCustomization.id,
    };
    await FormSubmits.bulkSetItemsOfOneCustomization(setItemsArgs);

    // To refresh customizations in case the user browses a customization they just added this option to
    await actions.getAllResources(apiToken, 'customizations');

    if (typeof finalRedirectFunc === 'function') finalRedirectFunc(newCustomization.id);
  }

  updateOptionsList = (options) => {
    this.setState({ options });
  }

  validateFieldAndUpdateUI = (fieldKey, val) => {
    const field = resourceFieldsAsMap[fieldKey];
    const isInvalid = this.isFieldInvalid(field, val);
    const errorMessage = fieldKey === 'position'
      ? this.getPositionErrorMessage(isInvalid)
      : FieldValidation.getErrorMessage(field, isInvalid);
    const disableSaveButton = this.shouldDisableSaveButton(fieldKey, isInvalid);

    this.setState({
      disableSaveButton,
      [getFieldIsInvalidStateKey(fieldKey)]: isInvalid,
      [getInvalidFieldMessageStateKey(fieldKey)]: errorMessage,
    });
  }

  getPositionErrorMessage = (isInvalid) => {
    if (!isInvalid) return '';
    const { startingPosition } = this.state;
    if (startingPosition === 0) return 'Position must be a whole number, e.g. 1, 2.';
    if (startingPosition > 0) return 'Position must be a whole number e.g. 1, 2, and cannot be changed back to zero';
  }

  validateFieldsBeforeSubmit = () => {
    const newIsFieldInvalidMap = {};
    const fieldErrorMessageMap = {};
    let disableSaveButton = false;
    fieldKeysForIteration.forEach((fieldKey) => {
      const value = this.state[fieldKey];
      const field = resourceFieldsAsMap[fieldKey];
      const isInvalid = this.isFieldInvalid(field, value);
      if (isInvalid) disableSaveButton = true;
      newIsFieldInvalidMap[getFieldIsInvalidStateKey(fieldKey)] = isInvalid;
      const errorMessage = FieldValidation.getErrorMessage(field, isInvalid);
      fieldErrorMessageMap[getInvalidFieldMessageStateKey(fieldKey)] = errorMessage;
    });

    this.setState({ ...newIsFieldInvalidMap, ...fieldErrorMessageMap, disableSaveButton });
    return disableSaveButton;
  }

  renderOptionsSection = () => {
    const {
      actions,
      admin,
      apiToken,
      classes,
    } = this.props;
    const { options } = this.state;

    const isAddOptionAuthorized = Authorization.checkIsAuthorized(admin, 'options', null, Authorization.ADD);
    const isEditOptionAuthorized = Authorization.checkIsAuthorized(admin, 'options', null, Authorization.EDIT);

    const hasSharedOptions = CompanyFeatures.isSharedOptionsEnabled(admin);
    return (
      <React.Fragment>
        <div className={classes.optionScrollTarget} ref={el => this.optionScrollTarget = el} />
        <Typography classes={this.sectionTitleClasses}>Options</Typography>
        <Grid
          container
          item
        >
          {
            isAddOptionAuthorized
            && (
              <Button
                classes={this.addOptionButtonClasses}
                onClick={this.openCreateEditOptionDialog(DIALOG_TYPES.CREATE)}
              >
                CREATE OPTION
              </Button>
            )
          }
          {
            hasSharedOptions && isEditOptionAuthorized
            && (
              <Button
                classes={this.useExistingOptionButtonClasses}
                onClick={this.openUseExistingOptionDialog}
              >
                ADD EXISTING OPTIONS
              </Button>
            )
          }
        </Grid>
        {
          Array.isArray(options) && options.length > 0
          && (
            <DisplayResources
              actions={actions}
              admin={admin}
              apiToken={apiToken}
              data={options}
              fieldNames={resourceFields.options}
              hideToolbar
              hideBulkActions
              isFromCreateEditModifier
              onClickDeleteResource={this.onClickDeleteOption}
              onClickDuplicateResource={this.onClickDuplicateOption}
              onClickEditResource={this.onClickEditOption}
              resourceName="options"
            />
          )
        }
      </React.Fragment>
    );
  }

  render() {
    const {
      actions,
      admin,
      allOptions,
      apiToken,
      classes,
      customizations,
      type,
    } = this.props;
    const {
      createOrEditOption,
      disableSaveButton,
      isDialogOpen,
      optionId,
      options,
      originalModifierDetails,
      scrollToOptionTitle,
      searchableCustomizations,
      searchableLocations,
      searchableLocationsWithSelectAll,
      searchableOptions,
      whichDialogOpen,
    } = this.state;

    const pageTitle = type === 'create' ? 'New Modifier' : 'Edit Modifier';

    if (this.optionScrollTarget && scrollToOptionTitle) {
      this.optionScrollTarget.scrollIntoView();
      this.setState({ scrollToOptionTitle: false, hasScrolledToOptionTitle: true });
    }
    return (
      <React.Fragment>
        <section className={classes.pageTopSectionParent}>
          <section className={classes.pageTopSectionVerticalQuarter} />
          <section className={classes.pageTopSectionVerticalQuarter}>
            <Typography classes={this.pageTitleClasses}>{pageTitle}</Typography>
            <section className={classes.saveButtonsParent}>
              {type === 'create'
                && (
                  <React.Fragment>
                    <Button
                      classes={this.saveModifierButtonClasses}
                      color="primary"
                      disabled={disableSaveButton}
                      onClick={this.submitModifier(this.goToEditPageOfSameModifier)}
                      variant="outlined"
                    >
                      SAVE AND ADD OPTIONS
                    </Button>
                    <section className={classes.saveButtonsSpacer} />
                  </React.Fragment>
                )
              }
              <Button
                classes={this.saveModifierButtonClasses}
                color="primary"
                disabled={disableSaveButton}
                onClick={this.submitModifier(this.goToListCustomizations)}
                variant="outlined"
              >
                SAVE
              </Button>
            </section>
          </section>
          <section className={classes.pageTopSectionVerticalQuarter} />
          <section className={classes.pageTopSectionVerticalQuarter} />
        </section>
        <Grid
          classes={this.pageMainSectionClasses}
          container
          direction="column"
        >
          <Typography classes={this.sectionTitleClasses}>Modifier Information</Typography>
          <Paper classes={this.modifierFieldsPaperClasses}>
            {this.renderFieldSections()}
          </Paper>
          {type === 'edit' && this.renderOptionsSection()}
        </Grid>
        <UseExistingOptionDialog
          actions={actions}
          allOptions={allOptions}
          apiToken={apiToken}
          createOrEditModifier={type}
          originalModifierDetails={originalModifierDetails}
          originalOptions={options}
          isOpen={isDialogOpen && whichDialogOpen === 'useExistingOption'}
          onClose={this.closeDialogs}
          searchableOptions={searchableOptions}
          updateParentOptionsList={this.updateOptionsList}
        />
        <CreateEditOptionDialog
          actions={actions}
          admin={admin}
          apiToken={apiToken}
          customizations={customizations}
          createOrEdit={createOrEditOption}
          isFromCreateModifier={type === 'create'}
          isFromEditModifier={type === 'edit'}
          onClose={this.closeDialogs}
          onEditOptionForCreateEditModifier={this.replaceOptionInList}
          open={isDialogOpen && whichDialogOpen === 'createEditOption'}
          optionId={optionId}
          optionListFromCreateEditModifier={options}
          originalModifierDetails={originalModifierDetails}
          searchableCustomizations={searchableCustomizations}
          searchableLocations={searchableLocations}
          searchableLocationsWithSelectAll={searchableLocationsWithSelectAll}
          updateParentOptionsList={this.addOptionToList}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  admin: state.getIn(['admin', 'admin']),
  customizations: state.getIn(['resources', 'customizations']),
  allOptions: state.getIn(['resources', 'options']),
  pagination: state.getIn(['resources', 'pagination']),
});

const pipeline = flowRight([withWidth(), withRouter, withStyles(styles), connect(mapStateToProps)]);
export const CreateEditModifier = pipeline(BaseCreateEditModifier);
